import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import { SCREEN_WIDTH } from '../../utils/screensize';
import type { ReportDatePreset, ReportsFilterState, ReportSortType } from '../../typing/reportsFilter';
import {
  DEFAULT_REPORTS_FILTER_STATE,
  REPORT_DATE_PRESETS,
  SORT_TYPE_OPTIONS,
  reportDatePresetLabel
} from '../../typing/reportsFilter';

type AuditorOption = { id: string; name: string; email: string };
type SiteOption = { id: string; name: string };
type FilterCategory = 'auditor' | 'site' | 'date';
type ExpandedSection = FilterCategory | null;
type ChipCategory = 'auditor' | 'site';

type FilterChip = { key: string; label: string; onRemove: () => void };

type RowDescriptor = {
  key: FilterCategory;
  title: string;
  icon: string;
  iconTint: string;
  iconBg: string;
  subtitle: string;
  renderBody: () => React.ReactNode;
};

// Match the app's centered frame width (see routes.tsx / HomeCheckInModal)
const FRAME_WIDTH = SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH;

const ICON_THEME = {
  auditor: { tint: '#6938EF', bg: '#F4F3FF' },
  site: { tint: '#009e43', bg: '#E7F8EE' },
  date: { tint: '#175CD3', bg: '#EFF4FF' },
  sort: { tint: '#009e43', bg: '#E7F8EE' }
};

interface Props {
  visible: boolean;
  initialFilter: ReportsFilterState;
  availableAuditors: AuditorOption[];
  availableSites: SiteOption[];
  onApply: (filter: ReportsFilterState) => void;
  onClose: () => void;
}

function toggleItem<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter(v => v !== item) : [...arr, item];
}

function selectionSummary(values: string[], resolveLabel: (value: string) => string): string {
  if (values.length === 0) {
    return 'All';
  }
  if (values.length === 1) {
    return resolveLabel(values[0]);
  }
  return `${values.length} selected`;
}

function removeValue(filter: ReportsFilterState, category: ChipCategory, value: string): ReportsFilterState {
  switch (category) {
    case 'auditor':
      return { ...filter, auditors: filter.auditors.filter(v => v !== value) };
    case 'site':
      return { ...filter, sites: filter.sites.filter(v => v !== value) };
    default:
      return filter;
  }
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text style={styles.sectionLabel}>{children}</Text>
);

const TintedIcon: React.FC<{ name: string; tint: string; bg: string; size?: number }> = ({
  name,
  tint,
  bg,
  size = 20
}) => (
  <View style={[styles.iconSquare, { backgroundColor: bg }]}>
    <Icon name={name} size={size} color={tint} />
  </View>
);

const Chip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <View style={styles.chip}>
    <Icon name="filter-variant" size={13} color="#027A48" style={styles.chipIcon} />
    <Text style={styles.chipText} numberOfLines={1}>
      {label}
    </Text>
    <TouchableOpacity
      onPress={onRemove}
      style={styles.chipClose}
      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      activeOpacity={0.7}
    >
      <Icon name="close" size={14} color="#027A48" />
    </TouchableOpacity>
  </View>
);

const ActiveFilterChips: React.FC<{ chips: FilterChip[] }> = ({ chips }) => {
  if (chips.length === 0) {
    return null;
  }
  return (
    <>
      <SectionLabel>Active Filters</SectionLabel>
      <View style={styles.chipRow}>
        {chips.map(chip => (
          <Chip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
        ))}
      </View>
    </>
  );
};

const FilterCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.card}>{children}</View>
);

interface FilterRowProps {
  icon: string;
  iconTint: string;
  iconBg: string;
  title: string;
  subtitle: string;
  isExpanded: boolean;
  onPress: () => void;
}

const FilterRow: React.FC<FilterRowProps> = ({ icon, iconTint, iconBg, title, subtitle, isExpanded, onPress }) => (
  <TouchableOpacity style={styles.rowHeader} onPress={onPress} activeOpacity={0.7}>
    <TintedIcon name={icon} tint={iconTint} bg={iconBg} />
    <View style={styles.rowTextWrap}>
      <Text style={styles.rowTitle}>{title}</Text>
      <Text style={styles.rowSubtitle} numberOfLines={1}>
        {subtitle}
      </Text>
    </View>
    <Icon name={isExpanded ? 'chevron-up' : 'chevron-right'} size={22} color="#98A2B3" />
  </TouchableOpacity>
);

interface CheckRowProps {
  label: string;
  sublabel?: string;
  checked: boolean;
  onToggle: () => void;
}

const CheckRow: React.FC<CheckRowProps> = ({ label, sublabel, checked, onToggle }) => (
  <TouchableOpacity style={styles.checkRow} onPress={onToggle} activeOpacity={0.7}>
    <Icon
      name={checked ? 'checkbox-marked' : 'checkbox-blank-outline'}
      size={20}
      color={checked ? colors.primary : '#C4CDD9'}
      style={styles.checkRowIcon}
    />
    <View style={styles.checkRowTextWrap}>
      <Text style={[styles.checkRowLabel, checked ? styles.checkRowLabelActive : null]} numberOfLines={1}>
        {label}
      </Text>
      {sublabel ? (
        <Text style={styles.checkRowSublabel} numberOfLines={1}>
          {sublabel}
        </Text>
      ) : null}
    </View>
  </TouchableOpacity>
);

const ReportsFilterModal: React.FC<Props> = ({
  visible,
  initialFilter,
  availableAuditors,
  availableSites,
  onApply,
  onClose
}) => {
  const [draft, setDraft] = useState<ReportsFilterState>(initialFilter);
  const [expanded, setExpanded] = useState<ExpandedSection>(null);
  const [auditorSearch, setAuditorSearch] = useState('');
  const [siteSearch, setSiteSearch] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft(initialFilter);
      setExpanded(null);
      setAuditorSearch('');
      setSiteSearch('');
    }
  }, [visible, initialFilter]);

  const toggleSection = (section: ExpandedSection) => {
    setExpanded(prev => (prev === section ? null : section));
  };

  const auditorNameById = useMemo(() => {
    const map: Record<string, string> = {};
    availableAuditors.forEach(a => {
      map[a.id] = a.name;
    });
    return map;
  }, [availableAuditors]);

  const siteNameById = useMemo(() => {
    const map: Record<string, string> = {};
    availableSites.forEach(s => {
      map[s.id] = s.name;
    });
    return map;
  }, [availableSites]);

  const filteredAuditors = auditorSearch.trim()
    ? availableAuditors.filter(a => `${a.name} ${a.email}`.toLowerCase().includes(auditorSearch.toLowerCase()))
    : availableAuditors;

  const filteredSites = siteSearch.trim()
    ? availableSites.filter(s => s.name.toLowerCase().includes(siteSearch.toLowerCase()))
    : availableSites;

  const chips: FilterChip[] = useMemo(() => {
    const result: FilterChip[] = [];
    draft.auditors.forEach(id => {
      result.push({
        key: `auditor:${id}`,
        label: `Auditor: ${auditorNameById[id] ?? id}`,
        onRemove: () => setDraft(d => removeValue(d, 'auditor', id))
      });
    });
    draft.sites.forEach(id => {
      result.push({
        key: `site:${id}`,
        label: `Site: ${siteNameById[id] ?? id}`,
        onRemove: () => setDraft(d => removeValue(d, 'site', id))
      });
    });
    return result;
  }, [draft, auditorNameById, siteNameById]);

  const renderAuditorBody = () => (
    <View style={styles.expandedBody}>
      <View style={styles.searchWrap}>
        <Icon name="magnify" size={18} color="#98A2B3" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by auditor name..."
          placeholderTextColor="#B2B8C2"
          value={auditorSearch}
          onChangeText={setAuditorSearch}
        />
      </View>
      {filteredAuditors.length === 0 ? (
        <Text style={styles.emptySubText}>No auditors available</Text>
      ) : (
        <ScrollView style={styles.checkList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
          {filteredAuditors.map(auditor => (
            <CheckRow
              key={auditor.id}
              label={auditor.name}
              sublabel={auditor.email}
              checked={draft.auditors.includes(auditor.id)}
              onToggle={() => setDraft(d => ({ ...d, auditors: toggleItem(d.auditors, auditor.id) }))}
            />
          ))}
        </ScrollView>
      )}
      <View style={styles.catFooter}>
        <TouchableOpacity onPress={() => setDraft(d => ({ ...d, auditors: [] }))} activeOpacity={0.7}>
          <Text style={styles.catClear}>Clear</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setExpanded(null)} activeOpacity={0.7}>
          <Text style={styles.catDone}>Done</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderSiteBody = () => (
    <View style={styles.expandedBody}>
      <View style={styles.searchWrap}>
        <Icon name="magnify" size={18} color="#98A2B3" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search site..."
          placeholderTextColor="#B2B8C2"
          value={siteSearch}
          onChangeText={setSiteSearch}
        />
      </View>
      {filteredSites.length === 0 ? (
        <Text style={styles.emptySubText}>No sites available</Text>
      ) : (
        <ScrollView style={styles.checkList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
          {filteredSites.map(site => (
            <CheckRow
              key={site.id}
              label={site.name}
              checked={draft.sites.includes(site.id)}
              onToggle={() => setDraft(d => ({ ...d, sites: toggleItem(d.sites, site.id) }))}
            />
          ))}
        </ScrollView>
      )}
      <View style={styles.catFooter}>
        <TouchableOpacity onPress={() => setDraft(d => ({ ...d, sites: [] }))} activeOpacity={0.7}>
          <Text style={styles.catClear}>Clear</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setExpanded(null)} activeOpacity={0.7}>
          <Text style={styles.catDone}>Done</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const selectDatePreset = (preset: ReportDatePreset) => {
    if (preset === 'custom') {
      setDraft(d => ({ ...d, datePreset: 'custom' }));
      return;
    }
    setDraft(d => ({ ...d, datePreset: preset, customStart: null, customEnd: null }));
    setExpanded(null);
  };

  const renderDateBody = () => (
    <View style={styles.expandedBody}>
      <ScrollView style={styles.checkList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
        {REPORT_DATE_PRESETS.map(opt => (
          <View key={opt.value}>
            <TouchableOpacity style={styles.pickerRow} onPress={() => selectDatePreset(opt.value)} activeOpacity={0.7}>
              <Text style={[styles.pickerLabel, draft.datePreset === opt.value ? styles.pickerLabelActive : null]}>
                {opt.label}
              </Text>
              {draft.datePreset === opt.value && <Icon name="check" size={18} color={colors.primary} />}
            </TouchableOpacity>
            {opt.value === 'custom' && draft.datePreset === 'custom' && (
              <View style={styles.customDateWrap}>
                <TextInput
                  style={styles.customDateInput}
                  placeholder="Start (YYYY-MM-DD)"
                  placeholderTextColor="#B2B8C2"
                  value={draft.customStart ?? ''}
                  onChangeText={text => setDraft(d => ({ ...d, customStart: text || null }))}
                />
                <Text style={styles.customDateDash}>–</Text>
                <TextInput
                  style={styles.customDateInput}
                  placeholder="End (YYYY-MM-DD)"
                  placeholderTextColor="#B2B8C2"
                  value={draft.customEnd ?? ''}
                  onChangeText={text => setDraft(d => ({ ...d, customEnd: text || null }))}
                />
              </View>
            )}
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const filterRows: RowDescriptor[] = [
    {
      key: 'auditor',
      title: 'Auditor',
      icon: 'account-outline',
      iconTint: ICON_THEME.auditor.tint,
      iconBg: ICON_THEME.auditor.bg,
      subtitle: selectionSummary(draft.auditors, id => auditorNameById[id] ?? id),
      renderBody: renderAuditorBody
    },
    {
      key: 'site',
      title: 'Site',
      icon: 'office-building',
      iconTint: ICON_THEME.site.tint,
      iconBg: ICON_THEME.site.bg,
      subtitle: selectionSummary(draft.sites, id => siteNameById[id] ?? id),
      renderBody: renderSiteBody
    },
    {
      key: 'date',
      title: 'Date',
      icon: 'calendar-blank-outline',
      iconTint: ICON_THEME.date.tint,
      iconBg: ICON_THEME.date.bg,
      subtitle: reportDatePresetLabel(draft.datePreset, draft.customStart, draft.customEnd),
      renderBody: renderDateBody
    }
  ];

  const renderFilterCards = () => {
    type Group = { type: 'collapsed'; rows: RowDescriptor[] } | { type: 'expanded'; row: RowDescriptor };
    const groups: Group[] = [];
    let run: RowDescriptor[] = [];
    filterRows.forEach(row => {
      if (row.key === expanded) {
        if (run.length) {
          groups.push({ type: 'collapsed', rows: run });
          run = [];
        }
        groups.push({ type: 'expanded', row });
      } else {
        run.push(row);
      }
    });
    if (run.length) {
      groups.push({ type: 'collapsed', rows: run });
    }

    return groups.map((group, gi) => {
      if (group.type === 'expanded') {
        const { row } = group;
        return (
          <FilterCard key={row.key}>
            <FilterRow
              icon={row.icon}
              iconTint={row.iconTint}
              iconBg={row.iconBg}
              title={row.title}
              subtitle={row.subtitle}
              isExpanded
              onPress={() => toggleSection(row.key)}
            />
            {row.renderBody()}
          </FilterCard>
        );
      }
      return (
        <FilterCard key={`grp-${gi}-${group.rows[0].key}`}>
          {group.rows.map((row, i) => (
            <React.Fragment key={row.key}>
              {i > 0 && <View style={styles.cardDivider} />}
              <FilterRow
                icon={row.icon}
                iconTint={row.iconTint}
                iconBg={row.iconBg}
                title={row.title}
                subtitle={row.subtitle}
                isExpanded={false}
                onPress={() => toggleSection(row.key)}
              />
            </React.Fragment>
          ))}
        </FilterCard>
      );
    });
  };

  const setSort = (value: ReportSortType) => setDraft(d => ({ ...d, sortType: value }));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.root}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.backButton} activeOpacity={0.7}>
            <Icon name="arrow-left" size={22} color="#25282B" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Filter & Sort</Text>
          <View style={styles.headerRight} />
        </View>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Active filters */}
          <ActiveFilterChips chips={chips} />

          {/* Filters */}
          <SectionLabel>Filters</SectionLabel>
          {renderFilterCards()}

          {/* Sort by */}
          <SectionLabel>Sort by</SectionLabel>
          <FilterCard>
            {SORT_TYPE_OPTIONS.map((opt, i) => (
              <React.Fragment key={opt.value}>
                {i > 0 && <View style={styles.cardDivider} />}
                <TouchableOpacity
                  style={[styles.sortRow, draft.sortType === opt.value ? styles.sortRowActive : null]}
                  onPress={() => setSort(opt.value)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.sortLabel, draft.sortType === opt.value ? styles.sortLabelActive : null]}>
                    {opt.label}
                  </Text>
                  {draft.sortType === opt.value && <Icon name="check" size={20} color={colors.primary} />}
                </TouchableOpacity>
              </React.Fragment>
            ))}
          </FilterCard>
        </ScrollView>

        {/* Footer */}
        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.resetButton}
            onPress={() => {
              setDraft(DEFAULT_REPORTS_FILTER_STATE);
              setExpanded(null);
              setAuditorSearch('');
              setSiteSearch('');
            }}
            activeOpacity={0.7}
          >
            <Text style={styles.resetLabel}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.applyButton} onPress={() => onApply(draft)} activeOpacity={0.8}>
            <Text style={styles.applyLabel}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default ReportsFilterModal;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: FRAME_WIDTH,
    marginHorizontal: 'auto',
    backgroundColor: '#F4F6F8',
    flexDirection: 'column'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E9EDF3'
  },
  backButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTitle: {
    ...fontMaker({ weight: 'SemiBold' }),
    flex: 1,
    textAlign: 'center',
    fontSize: 17,
    color: '#25282B'
  },
  headerRight: {
    width: 36
  },
  scroll: {
    flex: 1,
    minHeight: 0 as any
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24
  },
  sectionLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 12,
    color: '#98A2B3',
    marginTop: 18,
    marginBottom: 8,
    marginLeft: 4
  },
  // Active filter chips
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#ECFDF3',
    borderRadius: 16,
    paddingLeft: 10,
    paddingRight: 6,
    paddingVertical: 5,
    marginRight: 8,
    marginBottom: 8
  },
  chipIcon: {
    marginRight: 5
  },
  chipText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: '#027A48',
    maxWidth: 220
  },
  chipClose: {
    marginLeft: 4,
    padding: 2
  },
  // Cards
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#E9EDF3',
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#0F172A',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#F0F2F5',
    marginLeft: 62
  },
  // Row header
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 14
  },
  iconSquare: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  rowTextWrap: {
    flex: 1
  },
  rowTitle: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: '#25282B'
  },
  rowSubtitle: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: '#667085',
    marginTop: 2
  },
  // Expanded body
  expandedBody: {
    borderTopWidth: 1,
    borderTopColor: '#F0F2F5',
    paddingHorizontal: 14,
    paddingTop: 4,
    paddingBottom: 10
  },
  // Search
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E9EDF3',
    paddingHorizontal: 10,
    marginTop: 10,
    marginBottom: 4
  },
  searchIcon: {
    marginRight: 6
  },
  searchInput: {
    ...fontMaker({ weight: 'Normal' }),
    flex: 1,
    height: 38,
    fontSize: 14,
    color: '#25282B'
  },
  checkList: {
    maxHeight: 260
  },
  emptySubText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: colors.gray,
    paddingVertical: 12,
    textAlign: 'center'
  },
  // Checkbox rows
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10
  },
  checkRowIcon: {
    marginRight: 10
  },
  checkRowTextWrap: {
    flex: 1
  },
  checkRowLabel: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 14,
    color: '#667085'
  },
  checkRowLabelActive: {
    ...fontMaker({ weight: 'SemiBold' }),
    color: '#25282B'
  },
  checkRowSublabel: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 12,
    color: '#98A2B3',
    marginTop: 1
  },
  // Date preset picker rows
  pickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12
  },
  pickerLabel: {
    ...fontMaker({ weight: 'Normal' }),
    flex: 1,
    fontSize: 14,
    color: '#667085'
  },
  pickerLabelActive: {
    ...fontMaker({ weight: 'SemiBold' }),
    color: '#25282B'
  },
  customDateWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 10
  },
  customDateInput: {
    ...fontMaker({ weight: 'Normal' }),
    flex: 1,
    height: 38,
    fontSize: 13,
    color: '#25282B',
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E9EDF3',
    paddingHorizontal: 10
  },
  customDateDash: {
    ...fontMaker({ weight: 'Normal' }),
    marginHorizontal: 8,
    color: '#98A2B3'
  },
  // Category footer (Clear / Done)
  catFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 10,
    marginTop: 2,
    borderTopWidth: 1,
    borderTopColor: '#F0F2F5'
  },
  catClear: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: '#667085'
  },
  catDone: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: colors.primaryDark
  },
  // Sort rows
  sortRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 15
  },
  sortRowActive: {
    backgroundColor: '#F0FDF4'
  },
  sortLabel: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 15,
    color: '#25282B'
  },
  sortLabelActive: {
    ...fontMaker({ weight: 'SemiBold' }),
    color: '#25282B'
  },
  // Footer
  footer: {
    flexShrink: 0 as any,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: '#E9EDF3',
    backgroundColor: '#fff',
    gap: 12
  },
  resetButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center'
  },
  resetLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: colors.primary
  },
  applyButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center'
  },
  applyLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: '#fff'
  }
});
